import { Dispatch, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { type Locale, format } from 'date-fns'
import { enUS, es } from 'date-fns/locale'
import { Bed, Euro, PawPrint, PhoneIcon, Scissors as ScissorsIcon, Truck } from 'lucide-react'

import { ReservationBadge } from '@/entities/reservation/ui/ReservationBadge'
import { acceptBudget } from '@/shared/api/reservationsAPI'
import { useGlobalPublicConfig } from '@/shared/hooks/use-global-config'
import { cn } from '@/shared/lib/styles/class-merge.ts'
import { Button } from '@/shared/ui/button.tsx'
import { HotelReservation, Reservation } from '@monorepo/functions/src/types/reservations'
import { AdditionalService, DriverService } from '@monorepo/functions/src/types/services'

type ExternalClientReservationCardProps = {
    reservation: Reservation
    setClientReservations: Dispatch<SetStateAction<Reservation[]>>
}

const isHotelReservation = (reservation: Reservation): reservation is HotelReservation => {
    return 'type' in reservation && reservation.type === 'hotel'
}

const isDriverService = (service: AdditionalService): service is DriverService => {
    return service.type === 'driver'
}

const LOCALE_FROM_LANGUAGE: Record<string, Locale> = {
    es: es,
    en: enUS,
}

export const ExternalClientReservationCard = ({
    reservation,
    setClientReservations,
}: ExternalClientReservationCardProps) => {
    const { t, i18n } = useTranslation(undefined, { keyPrefix: 'externalClient' })
    const [isExpanded, setIsExpanded] = useState(false)
    const [isAcceptingBudget, setIsAcceptingBudget] = useState(false)

    const { data: publicConfig } = useGlobalPublicConfig()
    const supportPhoneNumber = publicConfig.phoneNumber

    const formatDate = (date?: string) => {
        if (!date) return ''
        return format(new Date(date), 'dd MMM yyyy', { locale: LOCALE_FROM_LANGUAGE[i18n.language] })
    }

    const handleAcceptBudget = async () => {
        if (isAcceptingBudget) return

        setIsAcceptingBudget(true)
        try {
            const result = await acceptBudget({ reservationId: reservation.id })

            // Update the local state with the confirmed reservation
            setClientReservations(prev => prev.map(res => (res.id === reservation.id ? result.data : res)))
        } catch (error) {
            console.error('Error accepting budget:', error)
            // TODO: Add proper error handling/toast notification
        } finally {
            setIsAcceptingBudget(false)
        }
    }

    const translateService = (service: AdditionalService) => {
        switch (service.type) {
            case 'driver': {
                const driverService = service as DriverService
                return driverService.serviceType === 'pickup'
                    ? t('driverPickup', 'Pickup service')
                    : driverService.serviceType === 'dropoff'
                      ? t('driverDropoff', 'Dropoff service')
                      : t('driverBoth', 'Pickup and dropoff service')
            }
            case 'special_food':
                return `${t('specialFood', 'Special food')} (${
                    service.foodType === 'refrigerated' ? t('refrigerated', 'Refrigerated') : t('frozen', 'Frozen')
                })`
            case 'medication':
                return t('medication', 'Medication') + (service.comment ? `: ${service.comment}` : '')
            case 'special_care':
                return t('specialCare', 'Special care') + (service.comment ? `: ${service.comment}` : '')
            case 'hairdressing':
                return (
                    t('hairdressing', 'Grooming') +
                    ': ' +
                    service.services
                        .map(s => {
                            switch (s) {
                                case 'bath_and_brush':
                                    return t('bathAndBrush', 'Bath and brush')
                                case 'bath_and_trim':
                                    return t('bathAndTrim', 'Bath and trim')
                                case 'stripping':
                                    return t('stripping', 'Stripping')
                                case 'deshedding':
                                    return t('deshedding', 'Deshedding')
                                case 'brushing':
                                    return t('brushing', 'Brushing')
                                case 'spa':
                                    return t('spa', 'Spa')
                                case 'spa_ozone':
                                    return t('spaOzone', 'Ozone spa')
                                default:
                                    return s
                            }
                        })
                        .join(', ')
                )
            default:
                return t('unknownService', 'Unknown service')
        }
    }

    const getTransportService = (services: AdditionalService[]) => {
        const transportService = services.find(isDriverService)
        if (!transportService) return null

        const getTransportText = () => {
            switch (transportService.serviceType) {
                case 'pickup':
                    return t('transportPickup', 'Pickup')
                case 'dropoff':
                    return t('transportDropoff', 'Dropoff')
                case 'both':
                    return t('transportBoth', 'Complete')
                default:
                    return ''
            }
        }

        return (
            <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                <Truck className='h-4 w-4' />
                <span>{getTransportText()}</span>
            </div>
        )
    }

    const isHotel = isHotelReservation(reservation)

    const renderDates = () => {
        if (isHotel) {
            return (
                <>
                    <div className='flex items-center text-sm text-muted-foreground'>
                        <span className='flex items-center'>
                            {t('from', 'From')} {formatDate(reservation.checkInDate)}
                        </span>
                    </div>
                    <div className='flex items-center text-sm text-muted-foreground'>
                        <span className='flex items-center'>
                            {t('to', 'To')} {formatDate(reservation.checkOutDate)}
                        </span>
                    </div>
                </>
            )
        } else if ('type' in reservation && reservation.type === 'peluqueria') {
            return (
                <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                    <span>{formatDate(reservation.date)}</span>
                    <span>{reservation.time}</span>
                </div>
            )
        } else {
            return <div>Unknown reservation type</div>
        }
    }

    const renderPets = () => {
        if (isHotel && 'pets' in reservation) {
            return reservation.pets.map((pet, index) => {
                const petServices =
                    'additionalServices' in reservation
                        ? reservation.additionalServices.filter(
                              service =>
                                  'petIndex' in service && service.petIndex === index && !isDriverService(service),
                          )
                        : []

                return (
                    <div key={index} className='mb-4 last:mb-0'>
                        <div className='mb-2 flex items-center font-semibold text-gray-700'>
                            <PawPrint className='mr-2 h-4 w-4 text-gray-500' />
                            {pet.name} ({pet.breed})
                        </div>
                        {petServices.length > 0 && (
                            <ul className='ml-6 space-y-1'>
                                {petServices.map((service, serviceIndex) => (
                                    <li key={serviceIndex} className='text-sm text-gray-600'>
                                        {translateService(service)}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )
            })
        } else if ('type' in reservation && reservation.type === 'peluqueria' && 'pet' in reservation) {
            const petServices =
                'additionalServices' in reservation
                    ? reservation.additionalServices.filter(
                          service => 'petIndex' in service && service.petIndex === 0 && !isDriverService(service),
                      )
                    : []

            return (
                <div className='mb-4'>
                    <div className='mb-2 flex items-center font-semibold text-gray-700'>
                        <PawPrint className='mr-2 h-4 w-4 text-gray-500' />
                        {reservation.pet.name} ({reservation.pet.breed})
                    </div>
                    {petServices.length > 0 && (
                        <ul className='ml-6 space-y-1'>
                            {petServices.map((service, serviceIndex) => (
                                <li key={serviceIndex} className='text-sm text-gray-600'>
                                    {translateService(service)}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )
        } else {
            return <div>No pet information available</div>
        }
    }

    return (
        <>
            <div
                className='cursor-pointer p-4 transition-colors hover:bg-accent/50'
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className='flex flex-col gap-2'>
                    <div className='flex items-start justify-between'>
                        <div className='flex items-start gap-3'>
                            <div className={cn('rounded-full p-2', isHotel ? 'bg-blue-100' : 'bg-red-100')}>
                                {isHotel ? (
                                    <Bed className={cn('h-5 w-5', 'text-blue-600')} />
                                ) : (
                                    <ScissorsIcon className={cn('h-5 w-5', 'text-red-600')} />
                                )}
                            </div>
                            <div>
                                <p className={cn('text-sm font-semibold', isHotel ? 'text-blue-600' : 'text-red-600')}>
                                    {isHotel
                                        ? t('hotelReservation', 'Hotel Reservation')
                                        : t('groomingReservation', 'Grooming Reservation')}
                                </p>
                                <div className='mt-1 flex flex-col gap-1'>{renderDates()}</div>
                            </div>
                        </div>
                        <div className='flex flex-col items-end gap-1'>
                            <ReservationBadge reservationStatus={reservation.status} />
                            {'additionalServices' in reservation && getTransportService(reservation.additionalServices)}
                        </div>
                    </div>

                    {isExpanded && (
                        <div className='mt-4'>
                            <div className='space-y-4'>
                                <div>
                                    <h4 className='mb-2 font-semibold'>{t('pets', 'Pets')}:</h4>
                                    <div className='space-y-2'>{renderPets()}</div>
                                </div>

                                {reservation.totalPrice && (
                                    <div>
                                        <h4 className='mb-1 font-semibold'>
                                            {t('estimatedPrice', 'Estimated price')}:
                                        </h4>
                                        <div className='flex items-center gap-2 text-gray-700'>
                                            <Euro className='h-4 w-4 text-gray-500' aria-hidden='true' />
                                            <span>{reservation.totalPrice.toFixed(2)}</span>
                                        </div>
                                    </div>
                                )}

                                {'status' in reservation && reservation.status === 'budget' && (
                                    <div className='mt-4 flex gap-2'>
                                        <Button
                                            onClick={e => {
                                                e.stopPropagation()
                                                handleAcceptBudget()
                                            }}
                                            disabled={isAcceptingBudget}
                                            className='flex-1'
                                        >
                                            {isAcceptingBudget
                                                ? t('acceptingBudget', 'Accepting...')
                                                : t('acceptBudget', 'Accept budget')}
                                        </Button>
                                        <Button
                                            variant='outline'
                                            onClick={e => {
                                                e.stopPropagation()
                                                window.open(`tel:${supportPhoneNumber}`, '_blank')
                                            }}
                                            className='flex-1'
                                        >
                                            <PhoneIcon className='mr-2 h-4 w-4' />
                                            {t('callUs', 'Call us')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}
